import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG, STORAGE_KEYS, ERROR_MESSAGES } from '../utils/config';
import { ApiResponse } from '../types';

/**
 * Cookie utility functions
 */
const CookieUtils = {
  set: (name: string, value: string, days: number = 7): void => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const isSecure = window.location.protocol === 'https:';
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/${isSecure ? ';secure' : ''};samesite=strict`;
  },

  get: (name: string): string | null => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  },

  remove: (name: string): void => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
};

/**
 * Token Manager for handling JWT tokens
 */
export class TokenManager {
  private static readonly ACCESS_TOKEN_KEY = STORAGE_KEYS.ACCESS_TOKEN;
  private static readonly REFRESH_TOKEN_KEY = STORAGE_KEYS.REFRESH_TOKEN;

  static getToken(): string | null {
    return CookieUtils.get(this.ACCESS_TOKEN_KEY);
  }

  static setToken(token: string): void {
    CookieUtils.set(this.ACCESS_TOKEN_KEY, token, 7); // 7 days expiry
  }

  static removeToken(): void {
    CookieUtils.remove(this.ACCESS_TOKEN_KEY);
    CookieUtils.remove(this.REFRESH_TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return CookieUtils.get(this.REFRESH_TOKEN_KEY);
  }

  static setRefreshToken(token: string): void {
    CookieUtils.set(this.REFRESH_TOKEN_KEY, token, 7); // 7 days expiry
  }

  static isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;

/**
 * API Client Class
 */
class ApiClient {
  private axiosInstance: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.axiosInstance = this.createAxiosInstance();
    this.setupInterceptors();
  }

  private createAxiosInstance(): AxiosInstance {
    return axios.create({
      baseURL: this.baseURL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token and collegeId
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = TokenManager.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add collegeId to requests that need it (exclude auth endpoints)
        const needsCollegeId = !config.url?.includes('/auth/') && !config.url?.includes('/colleges');
        if (needsCollegeId) {
          const collegeData = this.getCollegeDataFromStorage();
          if (collegeData?.id) {
            // For GET requests, add collegeId as query parameter
            if (config.method?.toUpperCase() === 'GET') {
              const separator = config.url?.includes('?') ? '&' : '?';
              config.url = `${config.url}${separator}collegeId=${collegeData.id}`;
            }
            // For POST/PUT/DELETE requests, add collegeId to the data body
            else if (config.method?.toUpperCase() === 'POST' || config.method?.toUpperCase() === 'PUT') {
              if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
                config.data = {
                  ...config.data,
                  collegeId: collegeData.id
                };
              }
            }
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle common errors
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      (error) => {
        if (error.response) {
          const { status, config } = error.response;

          switch (status) {
            case HTTP_STATUS.UNAUTHORIZED:
              // Only redirect to login if this is NOT a login/register attempt
              // and user has a token (authenticated session expired)
              const isAuthEndpoint = config?.url?.includes('/auth/login') || 
                                    config?.url?.includes('/auth/register');
              
              if (!isAuthEndpoint && TokenManager.isAuthenticated()) {
                // Token expired or invalid - clear and redirect
                TokenManager.removeToken();
                window.location.href = '/login';
              }
              // For login attempts, just pass the error through
              break;
            case HTTP_STATUS.FORBIDDEN:
              console.error('Access forbidden:', error.response.data);
              break;
            case HTTP_STATUS.NOT_FOUND:
              console.error('Resource not found:', error.response.data);
              break;
            case HTTP_STATUS.INTERNAL_SERVER_ERROR:
              console.error('Server error:', error.response.data);
              break;
            default:
              console.error('API Error:', error.response.data);
          }
        } else if (error.request) {
          // Network error
          console.error('Network error:', error.request);
        } else {
          console.error('Error:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Generic request method
   */
  private async request<T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.request<ApiResponse<T>>({
        method,
        url,
        data,
        ...config,
      });

      return response.data;
    } catch (error: any) {
      // Transform axios error to our API response format
      if (error.response?.data) {
        throw error.response.data;
      }

      // Network or other errors
      const apiError: ApiResponse = {
        success: false,
        message: error.message || ERROR_MESSAGES.NETWORK_ERROR,
        data: null,
      };
      throw apiError;
    }
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('GET', url, undefined, config);
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('POST', url, data, config);
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', url, data, config);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', url, undefined, config);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', url, data, config);
  }

  /**
   * Upload file
   */
  async uploadFile<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<T>('POST', url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }

  /**
   * Get base URL for constructing full URLs
   */
  getBaseURL(): string {
    return this.baseURL;
  }

  /**
   * Set auth token manually
   */
  setAuthToken(token: string): void {
    TokenManager.setToken(token);
  }

  /**
   * Remove auth token
   */
  removeAuthToken(): void {
    TokenManager.removeToken();
  }

  /**
   * Check if authenticated
   */
  isAuthenticated(): boolean {
    return TokenManager.isAuthenticated();
  }

  /**
   * Get college data from localStorage for use in request interceptor
   */
  private getCollegeDataFromStorage(): { id: string; name: string; logo?: string } | null {
    try {
      const stored = localStorage.getItem('unii_college_data');
      if (stored) {
        return JSON.parse(stored);
      }
      return null;
    } catch (error) {
      console.error('Failed to retrieve college data:', error);
      return null;
    }
  }
}

// Create and export API client instance
export const apiClient = new ApiClient();

// Export types for use in services
export type { ApiResponse };

/**
 * Error handler utility
 */
export const handleApiError = (error: any): string => {
  // Handle 429 Too Many Requests - Rate Limit
  if (error?.response?.status === 429) {
    return error?.response?.data?.message || 'You are doing that too fast. Please slow down and try again in a moment.';
  }

  // Handle 401 Unauthorized - Invalid credentials
  if (error?.response?.status === 401) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }

  // Handle 403 Forbidden
  if (error?.response?.status === 403) {
    return error?.response?.data?.message || 'Access denied. Please contact your administrator.';
  }

  if (error?.message) {
    return error.message;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.errors && Array.isArray(error.errors) && error.errors.length > 0) {
    return error.errors.map((err: any) => err.message).join(', ');
  }

  return ERROR_MESSAGES.NETWORK_ERROR;
};

/**
 * Success handler utility
 */
export const handleApiSuccess = <T>(response: ApiResponse<T>): T => {
  if (!response.success) {
    throw new Error(response.message || 'API request failed');
  }

  return response.data as T;
};

/**
 * Retry utility for failed requests
 */
export const retryRequest = async <T>(
  requestFn: () => Promise<T>,
  maxRetries: number = API_CONFIG.RETRY_ATTEMPTS,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;

  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;

      if (i === maxRetries) {
        break;
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
    }
  }

  throw lastError;
};